import { useId, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface MeterSegment {
  label: string;
  value: number;
  /** chart-1 bis chart-5, sonst nach Reihenfolge. */
  color?: 1 | 2 | 3 | 4 | 5;
}

export interface MeterProps {
  label?: ReactNode;
  /** 0–100 */
  value?: number;
  valueLabel?: ReactNode;
  tone?: 'selection' | 'success' | 'warning' | 'danger';
  /** Gestapelt, höchstens fünf Segmente. */
  segments?: MeterSegment[];
  legend?: boolean;
  /**
   * Name für Screenreader, wenn kein sichtbares `label` dasteht (Score in
   * einer Tabellenzeile: „Score 72 von 100“). Mit `label` heißt der Balken
   * wie sein Label.
   */
  ariaLabel?: string;
  className?: string;
}

const FUELLUNG = { selection: 'bg-selection', success: 'bg-success', warning: 'bg-warning', danger: 'bg-danger' };
const CHART = ['bg-chart-1', 'bg-chart-2', 'bg-chart-3', 'bg-chart-4', 'bg-chart-5'];
const prozent = (v: number) => (Math.round(v * 10) / 10).toLocaleString('de-DE') + ' %';

/** Anteil an einem Ganzen: ein Balken mit Wert oder gestapelte Segmente mit Legende. */
export function Meter({ label, value = 0, valueLabel, tone = 'selection', segments, legend = true, ariaLabel, className }: MeterProps) {
  const labelId = useId();
  if (segments) {
    const summe = segments.reduce((s, x) => s + x.value, 0) || 1;
    const farbe = (s: MeterSegment, i: number) => CHART[(s.color ?? i + 1) - 1] ?? CHART[0];
    return (
      <div className={cn('flex flex-col gap-3', className)}>
        {legend && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 t-small text-muted-foreground">
            {segments.map((s, i) => (
              <span key={s.label} className="inline-flex items-center gap-1.5">
                <span aria-hidden className={cn('size-2 shrink-0 rounded-full', farbe(s, i))} />
                {s.label} <b className="font-medium tabular text-foreground">{prozent((s.value / summe) * 100)}</b>
              </span>
            ))}
          </div>
        )}
        <div role="img" aria-label={segments.map((s) => `${s.label} ${s.value}`).join(', ')} className="flex h-2 gap-0.5">
          {segments.map((s, i) => (
            <span
              key={s.label}
              className={cn('block h-full min-w-[3px] first:rounded-l-full last:rounded-r-full', farbe(s, i))}
              style={{ width: `${(s.value / summe) * 100}%` }}
            />
          ))}
        </div>
      </div>
    );
  }
  const v = Math.max(0, Math.min(100, value));
  return (
    <div className={cn('grid items-center gap-3', label ? 'grid-cols-[minmax(56px,auto)_1fr_auto]' : 'grid-cols-[1fr_auto]', className)}>
      {label && (
        <span id={labelId} className="t-body-strong">
          {label}
        </span>
      )}
      <span
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={v}
        aria-label={label ? undefined : ariaLabel}
        aria-labelledby={label ? labelId : undefined}
        className="relative block h-1.5 overflow-hidden rounded-full bg-accent">
        <span className={cn('block h-full rounded-[inherit]', FUELLUNG[tone])} style={{ width: `${v}%` }} />
      </span>
      <span className="min-w-11 text-right font-mono text-[13px] font-medium leading-[18px] tabular text-foreground">{valueLabel ?? prozent(v)}</span>
    </div>
  );
}
